// components/funding/ExportListModal.jsx
import React, { useState, useRef } from 'react';
import api from '../../services/api';
import { usePostHog } from '@posthog/react';

const EXPORT_MAX_ITEMS = 30;

const ALL_FIELDS = [
  { key: 'oko_url', label: 'Oko URL' },
  { key: 'name', label: 'Name' },
  { key: 'fund_collection', label: 'Fund Collection' },
  { key: 'funders', label: 'Funder' },
  { key: 'deadline_date', label: 'Deadline' },
  { key: 'duration', label: 'Duration' },
  { key: 'payment_amount', label: 'Payment Amount' },
  { key: 'must_do', label: 'Must Do' },
  { key: 'must_not_do', label: 'Must Not Do' },
];

const DEFAULT_KEYS = ALL_FIELDS.map(f => f.key);

const ExportListModal = ({ list, onCancel }) => {
  const posthog = usePostHog();
  const [selected, setSelected] = useState(
    ALL_FIELDS.filter(f => DEFAULT_KEYS.includes(f.key))
  );
  const [format, setFormat] = useState('csv');
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const itemCount = list.items_count ?? 0;
  const overLimit = itemCount > EXPORT_MAX_ITEMS;
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragDirection, setDragDirection] = useState(null);
  const dragNode = useRef(null);

  const selectedKeys = new Set(selected.map(f => f.key));
  const unselected = ALL_FIELDS.filter(f => !selectedKeys.has(f.key));

  const addField = (field) => {
    setSelected(prev => [...prev, field]);
  };

  const removeField = (key) => {
    setSelected(prev => prev.filter(f => f.key !== key));
  };

  const selectAll = () => setSelected([...ALL_FIELDS]);
  const clearAll = () => setSelected([]);

  // Drag handlers for reordering selected fields
  const handleDragStart = (e, index) => {
    setDragIndex(index);
    dragNode.current = e.target;
    e.dataTransfer.effectAllowed = 'move';
    // Slight delay so drag image renders properly
    setTimeout(() => dragNode.current?.classList.add('opacity-50'), 0);
  };

  const handleDragEnter = (e, index) => {
    if (index !== dragIndex) {
      setDragDirection(index > dragIndex ? 'below' : 'above');
      setDragOverIndex(index);
    }
  };

  const handleDragEnd = () => {
    if (dragOverIndex !== null && dragOverIndex !== dragIndex) {
      setSelected(prev => {
        const next = [...prev];
        const [moved] = next.splice(dragIndex, 1);
        next.splice(dragOverIndex, 0, moved);
        return next;
      });
    }
    dragNode.current?.classList.remove('opacity-50');
    setDragIndex(null);
    setDragOverIndex(null);
    setDragDirection(null);
    dragNode.current = null;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleExport = async ({ truncate = false } = {}) => {
    if (selected.length === 0) return;
    setExporting(true);
    setError(null);
    try {
      const params = { file_format: format, fields: selected.map(f => f.key).join(',') };
      if (truncate) params.truncate = 'true';
      const response = await api.get(`/funding-lists/${list.id}/export/`, {
        params,
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${list.name}.${format === 'xlsx' ? 'xlsx' : 'csv'}`;
      a.click();
      window.URL.revokeObjectURL(url);
      posthog.capture('funding_list_exported', {
        list_id: list.id,
        format,
        column_count: selected.length,
        item_count: itemCount,
      });
      onCancel();
    } catch (err) {
      if (err.response?.data instanceof Blob) {
        const text = await err.response.data.text();
        try {
          const json = JSON.parse(text);
          setError(json.error || 'Export failed. Please try again.');
        } catch {
          setError('Export failed. Please try again.');
        }
      } else {
        setError(err.response?.data?.error || 'Export failed. Please try again.');
      }
    } finally {
      setExporting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 flex flex-col"
        style={{ fontFamily: "'Poppins', system-ui, sans-serif", maxHeight: '85vh' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-gray-100">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Export List</h3>
              <p className="text-sm text-gray-400 mt-0.5">
                {list.name} · {list.items_count ?? 0} opportunities
              </p>
            </div>
            {/* Format toggle */}
            <div className="flex gap-1.5 bg-gray-100 rounded-lg p-1">
              {['csv', 'xlsx'].map(f => (
                <button
                  key={f}
                  onClick={() => setFormat(f)}
                  className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${
                    format === f
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {f.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-hidden flex flex-col min-h-0 px-6 py-4 gap-4">

          {/* Selected fields */}
          <div className="flex flex-col min-h-0" style={{ flex: selected.length > 0 ? '1 1 auto' : '0 0 auto' }}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Selected · {selected.length}
              </span>
              <button
                onClick={selected.length === ALL_FIELDS.length ? clearAll : selectAll}
                className="text-xs font-medium hover:underline"
                style={{ color: '#615DEC' }}
              >
                {selected.length === ALL_FIELDS.length ? 'Clear all' : 'Select all'}
              </button>
            </div>

            {selected.length === 0 ? (
              <div className="border-2 border-dashed border-gray-200 rounded-xl py-4 text-center">
                <p className="text-xs text-gray-400">No columns selected</p>
              </div>
            ) : (
              <div
                className="overflow-y-auto rounded-xl border border-gray-200 divide-y divide-gray-100"
                onDragOver={handleDragOver}
              >
                {selected.map((field, index) => (
                  <div key={field.key}>
                    {/* Drop indicator above */}
                    {dragOverIndex === index && dragDirection === 'above' && (
                      <div className="h-0.5 mx-3 rounded-full" style={{ backgroundColor: '#615DEC' }} />
                    )}
                    <div
                      draggable
                      onDragStart={e => handleDragStart(e, index)}
                      onDragEnter={e => handleDragEnter(e, index)}
                      onDragEnd={handleDragEnd}
                      className={`flex items-center gap-3 px-3 py-2.5 bg-white hover:bg-gray-50 cursor-grab active:cursor-grabbing transition-colors ${
                        dragIndex === index ? 'opacity-40' : ''
                      }`}
                    >
                      {/* Drag handle */}
                      <svg className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" fill="currentColor" viewBox="0 0 16 16">
                        <circle cx="5" cy="4" r="1.2"/><circle cx="11" cy="4" r="1.2"/>
                        <circle cx="5" cy="8" r="1.2"/><circle cx="11" cy="8" r="1.2"/>
                        <circle cx="5" cy="12" r="1.2"/><circle cx="11" cy="12" r="1.2"/>
                      </svg>
                      <span className="px-2 py-0.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-md inline">
                        {field.label}
                      </span>
                      <div className="flex-1" />
                      <button
                        onClick={() => removeField(field.key)}
                        className="text-gray-300 hover:text-gray-500 transition-colors flex-shrink-0 p-0.5"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    {/* Drop indicator below */}
                    {dragOverIndex === index && dragDirection === 'below' && (
                      <div className="h-0.5 mx-3 rounded-full" style={{ backgroundColor: '#615DEC' }} />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Available fields */}
          {unselected.length > 0 && (
            <div className="flex flex-col" style={{ flex: '0 0 auto', maxHeight: '180px' }}>
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                Available
              </span>
              <div className="overflow-y-auto">
                <div className="flex flex-wrap gap-1.5">
                  {unselected.map(field => (
                    <button
                      key={field.key}
                      onClick={() => addField(field)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
                      </svg>
                      {field.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 pt-4 border-t border-gray-100">
          {overLimit && (
            <p className="text-sm text-amber-600 mb-3">
              This list has {itemCount} opportunities but export is limited to {EXPORT_MAX_ITEMS}. You can export the first {EXPORT_MAX_ITEMS}.
            </p>
          )}
          {error && (
            <p className="text-sm text-red-600 mb-3">{error}</p>
          )}
          <div className="flex gap-2 justify-end">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            {overLimit ? (
              <button
                onClick={() => handleExport({ truncate: true })}
                disabled={selected.length === 0 || exporting}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-all disabled:opacity-50"
                style={{ backgroundColor: '#615DEC' }}
              >
                {exporting ? 'Exporting...' : `Export first ${EXPORT_MAX_ITEMS}`}
              </button>
            ) : (
              <button
                onClick={() => handleExport()}
                disabled={selected.length === 0 || exporting}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-all disabled:opacity-50"
                style={{ backgroundColor: '#615DEC' }}
              >
                {exporting ? 'Exporting...' : `Export ${selected.length} column${selected.length !== 1 ? 's' : ''}`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportListModal;